import { Attendee, Class } from "../redux/types/types";

/**
 * Gets the appropriate access count for an attendee in relation to a specific class
 * @param attendee - The attendee object
 * @param classData - The class object (optional, if not provided returns general remaining classes)
 * @returns The access count for the specific class or general remaining classes
 */
export const getAttendeeAccessCount = (
  attendee: Attendee,
  classData?: Class | null
): number => {
  // If no class is specified, return general remaining classes
  if (!classData) {
    return attendee.remainingClasses || 0;
  }

  // Logic for requiresPayment classes (ALL attendees must pay)
  if (classData.requiresPayment) {
    // If it's a special class requiring payment, use specialClassAccess
    if (classData.isSpecial) {
      const specialAccess = attendee.specialClassAccess as {
        [classId: string]: number;
      } | null;
      if (!specialAccess) {
        return 0;
      }
      return specialAccess[classData.id.toString()] || 0;
    }
    // If it's a regular class requiring payment, use remainingClasses
    return attendee.remainingClasses || 0;
  }

  // Logic for NON-requiresPayment classes

  // For special classes (without requiresPayment), get the specific special access count for Other status
  // PermisS attendees get free access to special classes
  if (classData.isSpecial) {
    if (attendee.permisStatus === "PermisS") {
      return 999; // Unlimited access for PermisS to special classes
    }
    const specialAccess = attendee.specialClassAccess as {
      [classId: string]: number;
    } | null;
    if (!specialAccess) {
      return 0;
    }
    return specialAccess[classData.id.toString()] || 0;
  }

  // For regular classes (without requiresPayment), return general remaining classes
  return attendee.remainingClasses || 0;
};

/**
 * Gets a formatted display string for attendee access
 * @param attendee - The attendee object
 * @param classData - The class object (optional)
 * @param formatMessage - The intl formatMessage function
 * @returns Formatted string showing the appropriate access count
 */
export const getAttendeeAccessDisplay = (
  attendee: Attendee,
  classData: Class | null | undefined,
  formatMessage: (descriptor: { id: string }) => string
): string => {
  // For PermisS attendees, only display if class requires payment
  if (attendee.permisStatus === "PermisS") {
    if (!classData?.requiresPayment) {
      return ""; // Don't display for PermisS when no payment required
    }
    // If requiresPayment is true, continue to display below
  }

  const accessCount = getAttendeeAccessCount(attendee, classData);

  // For special classes (with or without requiresPayment), show special access
  if (classData?.isSpecial) {
    return `${formatMessage({
      id: "attendee.specialClassAccess",
    })}: ${accessCount}`;
  }

  // For regular (common) classes (with or without requiresPayment), show remaining classes
  return `${formatMessage({
    id: "attendee.remainingNonSpecialClasses",
  })}: ${accessCount}`;
};

/**
 * Gets special class access information for display in payment dialog
 * @param attendee - The attendee object
 * @param allClasses - Array of all available classes
 * @param formatMessage - The intl formatMessage function
 * @returns Array of strings showing special class access information
 */
export const getSpecialClassAccessForPayment = (
  attendee: Attendee,
  allClasses: Class[],
  formatMessage: (descriptor: { id: string }) => string
): string[] => {
  const specialAccess = attendee.specialClassAccess as {
    [classId: string]: number;
  } | null;

  if (!specialAccess || Object.keys(specialAccess).length === 0) {
    // No special class accesses - display "Special Class Access: 0"
    return [`${formatMessage({ id: "attendee.specialClassAccess" })}: 0`];
  }

  // Has special accesses - display class names with their access counts
  const accessEntries = Object.entries(specialAccess)
    .filter(([_, count]) => count > 0)
    .map(([classIdStr, count]) => {
      const classId = parseInt(classIdStr);
      const classData = allClasses.find((cls) => cls.id === classId);

      if (!classData) {
        return `${formatMessage({
          id: "attendee.specialClassAccess",
        })} (Class ${classId}): ${count}`;
      }

      // Get class title from the JSON object
      const classTitle =
        classData.title?.en ||
        classData.title?.fr ||
        classData.title?.uk ||
        `Class ${classId}`;

      return `${classTitle}: ${count}`;
    });

  return accessEntries.length > 0
    ? accessEntries
    : [`${formatMessage({ id: "attendee.specialClassAccess" })}: 0`];
};

/**
 * Checks if an attendee has access to a specific class
 * @param attendee - The attendee object
 * @param classData - The class object
 * @returns True if the attendee has access, false otherwise
 */
export const hasClassAccess = (
  attendee: Attendee,
  classData: Class
): boolean => {
  // PermisS attendees can attend regular classes without requiresPayment for free
  if (
    attendee.permisStatus === "PermisS" &&
    !classData.isSpecial &&
    !classData.requiresPayment
  ) {
    return true;
  }

  const accessCount = getAttendeeAccessCount(attendee, classData);
  return accessCount > 0;
};

/**
 * Checks if an attendee should see class counter for a specific class in an attendee list
 * This is used when viewing a class's attendee list to determine which attendees should show counters
 * @param attendee - The attendee object
 * @param classData - The class being viewed
 * @param isEnrolled - Whether the attendee is currently enrolled in this class (not unenrolled)
 * @returns True if counter should be displayed for this attendee in this class context
 */
export const shouldShowCounterForClass = (
  attendee: Attendee,
  classData: Class | null | undefined,
  isEnrolled: boolean = true
): boolean => {
  // Don't show if not enrolled
  if (!isEnrolled) {
    return false;
  }

  // Don't show if no class data
  if (!classData) {
    return false;
  }

  // Always show for "Other" status attendees
  if (attendee.permisStatus === "Other") {
    return true;
  }

  // For PermisS attendees:
  // - Don't show for special classes without requiresPayment (shown separately in SpecialClassAccessDisplay)
  // - Show for requiresPayment classes (special or regular)
  // - Don't show for regular classes without requiresPayment (they get free access)
  if (attendee.permisStatus === "PermisS") {
    // Show counter if class requires payment (special or regular)
    if (classData.requiresPayment) {
      return true;
    }
    // Don't show counter for special classes without requiresPayment (shown separately)
    if (classData.isSpecial) {
      return false;
    }
    // Don't show for regular classes without requiresPayment
    return false;
  }

  return false;
};

/**
 * Checks if an attendee should see class counter displays
 * For "Other" status: always show
 * For "PermisS": only show if they're enrolled in requiresPayment classes (special or regular)
 * Note: Special class access is shown separately via SpecialClassAccessDisplay component
 * @param attendee - The attendee object
 * @param enrolledClassIds - Array of class IDs the attendee is enrolled in (optional)
 * @param allClasses - Array of all available classes (optional, required if enrolledClassIds provided)
 * @returns True if payment buttons and counters should be displayed
 */
export const shouldShowClassCounters = (
  attendee: Attendee,
  enrolledClassIds?: number[],
  allClasses?: Class[]
): boolean => {
  // Always show for "Other" status
  if (attendee.permisStatus === "Other") {
    return true;
  }

  // For PermisS, ONLY show if they're enrolled in requiresPayment classes (special or regular)
  if (enrolledClassIds && allClasses) {
    const hasRequiresPaymentClass = enrolledClassIds.some((classId) => {
      const classData = allClasses.find((cls) => cls.id === classId);
      return classData && classData.requiresPayment;
    });
    if (hasRequiresPaymentClass) {
      return true;
    }
  }

  // Otherwise, don't show payment buttons for PermisS
  return false;
};
